import {
  DEVICE_CATEGORIES, SALE_MODES, UNIT_STATUSES, deriveOrderStatus,
  FORM_FACTOR_LABEL as FORM_FACTOR_LABEL_RAW,
  formFactorLabel as formFactorLabelRaw,
} from '@platform/shared-types';
import type { OrderStatus, UnitStatus, RegisterWelcome, UnitActivation } from '@platform/shared-types';

/** `SaleMode` has no exported alias in shared-types; derive it from the runtime tuple. */
type SaleMode = (typeof SALE_MODES)[number];
/**
 * DERIVED, not re-typed. This was a hand-written union of the same four values,
 * which is a second copy of the enum — and the copy is the one nobody updates.
 * Adding a category in shared-types now widens this automatically.
 */
export type DeviceCategory = (typeof DEVICE_CATEGORIES)[number];

// Self-contained client for hardware-service (device models + bundles, and the
// device orders / serial-numbered units behind fulfilment).
// Mirrors deviceConfigApi.ts conventions.
//
// NOTE the host shape: this service lives on the SHARED `platform` cluster and is
// addressed by BASE PATH (/hardware), not by a per-service subdomain. Do not
// "correct" this to hardware.api.<apex> — that pattern is closed (AWS domain cap).

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveHardwareHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/hardware-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/hardware`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/hardware`;
  return `https://platform.api.dev.${apex}/hardware`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_HARDWARE_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveHardwareHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    // Surface the server's own message — validation errors from the service are
    // the most useful thing a user can see here.
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error || '';
    } catch { /* non-JSON body */ }
    throw new Error(detail || `hardware ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// ---------------------------------------------------------------------------
// Shapes returned by the service. `landedCostCents` and the bundle cost fields
// are DERIVED server-side — never recomputed here, so the two cannot drift.
// ---------------------------------------------------------------------------

export interface DeviceModelRow {
  modelId: string;
  category: DeviceCategory;
  subCategory?: string;
  /** Additive (wave W2): brand taxonomy + low-stock threshold, both optional. */
  brand?: string;
  reorderPoint?: number;
  /**
   * serialized-vs-quantity-tracking wave. Absent = 'serialized' (every model
   * predating this field keeps today's exact behavior). Locks once stock
   * exists — hardware-service 409s a direction change either way.
   */
  trackingMode?: 'serialized' | 'quantity';
  /** The ONE barcode every unit of a 'quantity' model shares. Meaningless for 'serialized'. */
  sharedBarcode?: string;
  /**
   * Is this sold to tenants at all? (2026-08-30)
   *
   * ABSENT MEANS UNANSWERED, never `true` — every device predates the field.
   * Resolve it the way the stock list does: sellable only if the device
   * already carries offers. See `DeviceModelSchema.sellable`.
   */
  sellable?: boolean;
  manufacturer: string;
  modelName: string;
  description?: string;
  specs: Record<string, any>;
  media: { heroImageUrl?: string; photos: string[]; videos?: string[]; datasheetUrl?: string };
  /** Named attachments — the operator types each name; no fixed types. */
  documents?: { name: string; url: string; contentType?: string; sizeBytes?: number; uploadedAt?: number }[];
  economics: {
    unitCostCents: number;
    inboundShippingCents: number;
    dutyCents: number;
    defaultOutboundShippingCents: number;
  };
  /**
   * "Integrated with" — codes from `DEVICE_INTEGRATIONS`. Absent on every
   * device that predates the field; the schema defaults it to [].
   */
  integrations?: string[];
  vendor?: { supplierName?: string; supplierSku?: string; leadTimeDays?: number; moq?: number };
  appProfile?: {
    deviceType?: string;
    terminalFormFactor?: string;
    otaChannel?: string;
    minAppVersion?: string;
    /**
     * What the device calls itself — Android `ro.product.model` values, e.g.
     * `TC-TOUCH-K10`. A LIST: one model ships across firmware revisions that
     * report slightly different strings. Compared against a device's reported
     * `hardwareModel` to catch a unit registered as the wrong model; empty or
     * absent simply means there is nothing to check against.
     */
    hardwareIds?: string[];
    /**
     * Can this device take a custom boot animation? Absent means UNANSWERED,
     * which readers must treat as "assume it can" — every model predates it.
     * False on a Google-certified (GMS) device: installing one needs root.
     */
    supportsBootAnimation?: boolean;
  };
  /**
   * HOW THIS DEVICE TAKES A CARD — chosen on the device, read everywhere else.
   * One of the `PAYMENT_DEVICE_CONFIGS` ids. Only meaningful for category
   * 'payment_device'; `'none'` means checked in with no lane chosen yet.
   * Read it through `resolvePaymentConfig`, never on its own — absent means
   * nobody was ever asked, which is not the same as `'none'`.
   */
  paymentConfigId?: 'none' | 'dejavoo_spin_usb' | 'dejavoo_spin_cloud' | 'dejavoo_rubytill_pay' | 'dejavoo_cloud' | 'cloud' | 'nmi_direct_vp3350';
  /**
   * EVERY config this model MAY be registered as, when it is more than one.
   *
   * A Dejavoo P17 is the same physical terminal whether it is wired to the till
   * or reached over SPIn's cloud, and the catalog cannot know which — the unit
   * has not been to a store yet. So the model says what it CAN be and
   * registration records what it IS.
   *
   * Absent means "only `paymentConfigId`", which is every row written before
   * this field. Read it through `paymentConfigOptionsFor`, never on its own.
   */
  paymentConfigOptions?: Array<'none' | 'dejavoo_spin_usb' | 'dejavoo_spin_cloud' | 'dejavoo_rubytill_pay' | 'dejavoo_cloud' | 'cloud' | 'nmi_direct_vp3350'>;
  /** Derived from `paymentConfigId` and kept written for every existing reader. */
  paymentProfile?: { deviceClass: string; connectionType: string; creditMethod: string; emvMode: string };
  /** Advisory build-target hint from the catalog. Never authoritative — see installPlan.ts. */
  /** Null when explicitly cleared; both null and absent mean 'not configured'. */
  buildTarget?: string | null;
  compatibility: { worksWith: string[]; requires: string[] };
  lifecycle: { status: 'draft' | 'active' | 'eol' | 'discontinued'; eolDate?: number; replacedByModelId?: string; warrantyMonths?: number };
  displayOrder: number;
  landedCostCents: number;   // derived server-side
  createdAt: number;
  updatedAt: number;
}

export interface ModelUsage {
  modelId: string;
  modelName: string;
  status: string;
  idle: boolean;
  references: {
    bundles: { bundleId: string; name: string; status: string }[];
    skus: { skuId: string; name: string; via: 'model' | 'bundle'; status: string }[];
    /** null = could not be checked. NEVER treat as zero. */
    terminals: number | null;
    replacementFor: { modelId: string; modelName: string }[];
  };
  referenceCount: number | null;
  canDelete: boolean;
  reason: string;
}

export interface DeviceBundleRow {
  bundleId: string;
  name: string;
  description: string;
  heroImageUrl?: string;
  photos: string[];
  components: { modelId: string; quantity: number }[];
  combinedOutboundShippingCents: number;
  status: 'draft' | 'active' | 'eol';
  displayOrder: number;
  // derived server-side
  componentsLandedCents: number;
  totalCostCents: number;
  missing: string[];
  incompatible: string[];
  unmetRequires: string[];
  draftComponents: string[];
  retiredComponents: string[];
  createdAt: number;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// Device orders + serial-numbered units (Task B3's fulfilment API).
//
// The rule that shapes everything below: an ORDER's status is DERIVED from its
// units server-side and is never accepted from a client. `cancelled` is the one
// status this UI may set, and only before anything has shipped.
// ---------------------------------------------------------------------------

export interface DeviceOrderItemRow {
  skuId: string;
  bundleId?: string;
  modelId?: string;
  quantity: number;
  saleMode: SaleMode;
  amountCents: number;
  termMonths?: number;
}

/** One shipping box on an order (screen D). Contents are DERIVED from each unit's own `boxNo` — never stored here. */
export interface DeviceOrderBox {
  boxNo: number;
  carrier?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  weightGrams?: number;
  packedAt?: number;
  /**
   * serialized-vs-quantity-tracking wave — a quantity-mode model has no unit
   * to put in a box, so its packed quantity lives here instead, one entry
   * per model, merged (never duplicated) across repeated packs of the same
   * model into the same box. Absent on a box that predates this field or has
   * never packed a bulk line.
   */
  bulkLines?: { modelId: string; qty: number }[];
}

export interface DeviceOrderRow {
  orderId: string;
  /** `RB-1004382` — the number a human reads. Absent on orders placed before it existed. */
  orderNo?: string;
  tenantId: string;
  locationId: string;
  items: DeviceOrderItemRow[];
  /** DERIVED server-side from the unit states. Never send this back. */
  status: OrderStatus;
  /** True when the devices are NOT all in the same place. Read-only, derived on read. */
  partial?: boolean;
  carrier?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  shippedAt?: number;
  deliveredAt?: number;
  unitIds: string[];
  notes?: string;
  /**
   * Stamped by billing-service at order creation when the order contains a
   * financed device. `envelopeStatus` is a best-effort DISPLAY CACHE of the
   * envelope's last-known status — never the enforcement path (the packing
   * gate always does a live read against documents-service) — but it is
   * exactly what this list needs to show which orders are blocked at
   * Packing without a documents-service call per row (W5 review I8).
   */
  envelopeId?: string;
  envelopeStatus?: string;
  /** `'addon_order'` when ordered after signup — then it has its own subscription. */
  source?: string;
  /** An add-on order's own Stripe subscription, beside the store's plan. */
  addOnSubscriptionId?: string;
  /** The store's own subscription an add-on order sits beside. */
  parentSubscriptionId?: string;
  /** inventory-hub-unit-lifecycle wave (screen D) — one entry per shipping box. Absent on orders that predate packing. */
  boxes?: DeviceOrderBox[];
  /**
   * REPLACEMENTS STARTED AND NOT YET FINISHED, keyed by the OUTGOING unit id.
   * Written as the first act of the Replace wizard's "Undo it all" and removed
   * as the last act of its hand-over, so a wizard closed half-way can be
   * resumed from the order page. Absent = nothing pending.
   */
  pendingReplacements?: Record<string, PendingReplacementRow>;
  createdAt: number;
  updatedAt: number;
}

/**
 * One replacement in flight — mirrors `PendingReplacementSchema` in
 * shared-types. Everything the wizard discovered BEFORE it undid anything, so a
 * resume never has to re-discover it from a unit that is no longer attached.
 *
 * `terminalId` is the register the swap hands over: the device's OWN register
 * on the `pos` lane, and the paired till's register on the `payment` lane (a
 * reader's register is its till's). `cardTender` is the device-backed tender
 * that till had on before its reader was taken out of the pool.
 */
export interface PendingReplacementRow {
  unitId: string;
  outcome: 'good' | 'rma' | 'destroy' | 'lost';
  reason: string;
  lane: 'pos' | 'payment' | 'other';
  modelId?: string;
  terminalId?: string;
  provisionCode?: string;
  emvDeviceId?: string;
  cardTender?: string;
  varId?: string;
  applicationId?: string;
  partnerUnitId?: string;
  /** True when the device had already shipped — the field-replacement road. */
  postDispatch?: boolean;
  /** Stamped by the service. */
  startedAt: number;
  /** Stamped by the service. */
  actorEmail?: string;
}

export type ReplacementReason = 'returned' | 'lost_in_transit' | 'warranty' | 'damaged';

export interface DeviceUnitShipmentRow {
  carrier: string;
  trackingNumber: string;
  trackingUrl?: string;
  /** Stamped by the service the first time a unit reaches a shipped state, and never cleared. */
  shippedAt?: number;
}

/** One photographed receipt — see `DeviceUnitRow.receipts`. */
export interface DeviceUnitReceipt {
  receiptId: string;
  /** `label` is the device's own sticker, photographed at receiving. */
  kind: 'test' | 'refund' | 'label';
  /** S3 key. Links are presigned on read; a URL is never stored. */
  key: string;
  /** `label` photos ONLY — see `DeviceUnitSchema.receipts.url`. */
  url?: string;
  name?: string;
  contentType?: string;
  at: number;
  by: string;
}

export interface DeviceUnitRow {
  unitId: string;
  serialNumber?: string;
  /** Company-unique `RTA-######`, minted once at unit creation. Absent on a unit that predates this field. */
  trackingId?: string;
  /**
   * Bought labels stuck on this device — ALIASES, never identity. Many per
   * unit; `trackingId` above stays the one true id. See
   * `DeviceUnitSchema.externalBarcodes`.
   */
  externalBarcodes?: string[];
  modelId: string;
  state: UnitStatus;
  /** Where this physical unit came from — stamped when received against a GRN. Absent for manually-entered stock. */
  vendorId?: string;
  grnId?: string;
  /** What THIS unit cost — the LANDED figure. Falls back to the model's landedCostCents when absent. */
  costCents?: number;
  /**
   * How that landed figure was reached, when an operator entered it during
   * receiving. Additive and optional — units written before this have none,
   * and `costCents` above stays the number every consumer reads. Kept so the
   * cost editor reopens with the same three lines instead of a total someone
   * has to reverse-engineer. Outbound shipping is excluded: it is per SHIPMENT.
   */
  costBreakdown?: {
    unitCostCents: number;
    inboundShippingCents: number;
    dutyCents: number;
  };
  tenantId?: string;
  locationId?: string;
  terminalId?: string;
  /** The other half of the pair — see DeviceUnitSchema.coupledUnitId. */
  coupledUnitId?: string;
  /**
   * The name typed at the bench for the register this device will become,
   * saved before the register exists so a refresh does not discard it.
   * A DEFAULT only — the register's own name wins once it is created.
   */
  registerName?: string;
  orderId?: string;
  agreement?: {
    skuId: string;
    saleMode: SaleMode;
    /** Absent on an OPEN-ENDED rental — it bills until cancelled. */
    termMonths?: number;
    monthlyAmountCents: number;
    principalCents: number;
    paidCents: number;
    status: 'active' | 'settled' | 'waived' | 'defaulted' | 'cancelled';
    /** Both DERIVED server-side — never recompute them here. */
    progress?: number;
    outstandingCents?: number;
    /** Rent option (all additive; absent on pre-rental agreements). */
    rentalKind?: 'open_ended' | 'fixed_term';
    minCommitmentMonths?: number;
    depositCents?: number;
    depositRefundedCents?: number;
    cancelledAt?: number;
  };
  shipment?: DeviceUnitShipmentRow;
  /** Required while a unit is on_hold or backordered — and shown to the customer. */
  holdReason?: string;
  /** Set when a rental was cancelled: the device is out at the tenant and expected back. */
  returnExpectedAt?: number;
  /**
   * When this device reached the customer, and when its cover runs out.
   *
   * Both stamped ONCE by the delivery transition and pinned thereafter — see
   * DeviceUnitSchema. Absent means unknown, which is NOT the same as expired:
   * every unit delivered before the stamp existed has no record of when it
   * arrived, and it cannot be reconstructed. Render "unknown", never "expired".
   */
  deliveredAt?: number;
  warrantyUntil?: number;
  /** Which device this one was sent out to replace, and why. */
  replaces?: {
    unitId: string;
    reason: 'returned' | 'lost_in_transit' | 'warranty' | 'damaged';
    at: number;
    by?: string;
  };
  /** The other side of `replaces`, on the device that was replaced. */
  replacedByUnitId?: string;
  notes?: string;
  /**
   * The same note as a Quill Delta. Delta rather than HTML because this app
   * ships no sanitiser — see DeviceUnitSchema for the full reasoning. `notes`
   * above stays the plain text every other consumer reads.
   */
  notesRich?: { ops: any[] };
  /** Condition/QA badge ids currently on this unit. History lives in the audit trail. */
  badges?: string[];
  /** Additive identity fields (screen B) — editable through `upsertUnit`, same door as `serialNumber`. */
  mac?: string;
  firmwareNotes?: string;
  /** Additive device-health fields (B2) — a firmware change auto-appends a system timeline event. */
  osVersion?: string;
  /**
   * WHAT THE MACHINE SAID ABOUT ITSELF — written by the installer's adb probe,
   * never typed. Every field optional: the probe runs over a flaky link on
   * device builds that differ in which getprop keys exist, so a gap means
   * "this run did not learn it", never "the device does not have it".
   */
  hardware?: {
    capturedAt: number;
    model?: string;
    brand?: string;
    osVersion?: string;
    sdkInt?: number;
    cpuAbi?: string;
    fingerprint?: string;
    cpuModel?: string;
    cpuCores?: number;
    cpuMaxMhz?: number;
    ramMb?: number;
    storageTotalMb?: number;
    storageFreeMb?: number;
    wifiMac?: string;
    ethernetMac?: string;
    displayCount?: number;
    usbPeripherals?: string[];
  };
  appBuild?: string;
  lastSeenAt?: number;
  /** SERVER-OWNED — only `packUnitIntoBox` ever sets this; a plain `upsertUnit` body is pinned and cannot override it. */
  boxNo?: number;
  /**
   * THE VERIFIED OS INSTALL — SERVER-OWNED, only `recordOsInstall` writes it.
   *
   * Not the same kind of field as `osVersion`/`appBuild` above, which stay
   * hand-editable through `upsertUnit` (the "Identity & provenance" panel
   * writes them). A till's checklist is GATED on this one, so it has to be
   * unforgeable: the installer reports it, the service pins it, and no form
   * on any screen can invent one. Absent until somebody actually installs.
   */
  osInstall?: {
    appBuild: string;
    source: 'local' | 'dev' | 'prod';
    target: string;
    versionCode?: number;
    hash?: string;
    serial?: string;
    /** Whether the device's app data was wiped — see the schema's own note. */
    reset?: boolean;
    installedAt: number;
    by: string;
  };
  /** SERVER-OWNED — only `startProvisioning`/`tickProvisioningStep` ever write this. Absent until provisioning starts. */
  provisioning?: {
    modelId: string;
    startedAt: number;
    steps: ProvisioningStepState[];
  };
  /**
   * SERVER-OWNED — only `requestReinstall` writes this. Absent until somebody
   * sends the till back to be built again.
   *
   * Present AND newer than `osInstall.installedAt` means the reinstall has not
   * happened yet: the unit is sitting in Pending Configuration and its
   * checklist is locked. Once a new install is stamped, `osInstall` overtakes
   * it and this becomes the record of the last time it went round.
   */
  reinstall?: {
    requestedAt: number;
    requestedBy: string;
    reason?: string;
    fromState: string;
  };
  /**
   * THE PAIR'S TEST AND REFUND RECEIPTS, on its POS unit.
   *
   * Several per kind — one photo rarely shows a whole receipt. Absent on every
   * unit that predates the field and on every pair nobody has tested yet; read
   * as "no receipts", never as a failed test. Server-owned: written only by
   * `addReceipt` / `removeReceipt`.
   */
  receipts?: DeviceUnitReceipt[];
  /**
   * Damage lifecycle wave — DERIVED, only present on the single-unit
   * `GET /units?unitId=` response (`hardwareApi.getUnit`). Absent on every
   * `listUnits` row: a hub-wide list uses ONE `inventoryApi.listDamage({
   * status: 'open' })` fetch and maps the results onto units client-side
   * instead (see InventoryHubPage's Stock tab), rather than a damage scan
   * per row.
   */
  openDamageCount?: number;
  createdAt: number;
  updatedAt: number;
}

/** One step on a unit's SNAPSHOTTED checklist (screen B) — `category` is the label text, resolved once at snapshot time. */
export interface ProvisioningStepState {
  stepId: string;
  label: string;
  category: string;
  required: boolean;
  done: boolean;
  by?: string;
  at?: number;
  /**
   * The template step's how-to AS IT READ when provisioning started. This is
   * where it actually gets read — the person working the unit is looking at
   * this checklist, not at the template. Absent when the step has none.
   */
  instructions?: string;
}

// ---------------------------------------------------------------------------
// Provisioning templates (screen E) — per-model, per-category checklist.
// ---------------------------------------------------------------------------

export interface ProvisioningCategory {
  id: string;
  label: string;
  order?: number;
}

/** What `PUT /lifecycle/templates/{modelId}` accepts per step. */
export interface ProvisioningStepInput {
  stepId: string;
  label: string;
  categoryId: string;
  required?: boolean;
  order?: number;
  /**
   * Free-text how-to for whoever works this step. Snapshotted onto the unit
   * when provisioning starts, so it is read where the work happens rather than
   * only in this editor. Optional — steps written before it simply have none.
   */
  instructions?: string;
}

export interface ProvisioningTemplate {
  modelId: string;
  categories: ProvisioningCategory[];
  steps: ProvisioningStepInput[];
  updatedAt: number;
  updatedBy?: string;
}

// ---------------------------------------------------------------------------
// Unit timeline events (B2) — the normalized shape both the timeline GET and
// both event-injection POSTs use.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Stock movement ledger (serialized-vs-quantity-tracking wave) — the signed
// entries a quantity-mode model's on-hand/reserved are summed from. Mirrors
// `StockMovementSchema` in `packages/shared-types/src/deviceOrders.ts`.
// ---------------------------------------------------------------------------

export type StockMovementKind = 'received' | 'adjustment' | 'reserved' | 'released' | 'packed' | 'damaged';

export interface StockMovement {
  modelId: string;
  at: number;
  kind: StockMovementKind;
  /** Signed — see the per-kind convention documented on `inventoryApi.stockMovements`. */
  qty: number;
  reason?: string;
  note?: string;
  grnId?: string;
  orderId?: string;
  boxNo?: number;
  unitCostCents?: number;
  actor?: string;
}

export interface UnitEvent {
  unitId: string;
  at: number;
  kind: string;
  label: string;
  source: 'system' | 'admin' | 'device';
  actor?: string;
  meta?: Record<string, any>;
}

// -- "Hello from Ruby" welcome wizard (hardware-service, Task 3 contract) -----
/** The register's `welcome` map — shared-types `RegisterWelcome`. */
export type RegisterWelcomeRow = RegisterWelcome;
/** The order's lock as the admin read returns it: the code hash never leaves the service. */
export interface OrderWelcomeLockView {
  issuedAt: string;
  emailedAt?: string;
  emailedToMasked?: string;
  wrongTries: number;
  held: boolean;
  shipUnlocked: boolean;
}
export interface OrderWelcomeDevice {
  unitId: string;
  serial?: string;
  label: string;
  registerId?: string;
  registerName?: string;
  state: UnitStatus;
  locked: boolean;
  /** Who holds the lock, while locked. */
  lockedBy?: 'shipping' | 'super_admin';
  mode?: 'fresh' | 'setup';
  /** When the device first fetched its state while Fresh/locked — it has its lock cached. */
  deviceSeenAt?: string;
  activation?: UnitActivation;
}
export interface OrderWelcomeView {
  lock: OrderWelcomeLockView | null;
  devices: OrderWelcomeDevice[];
  activatedCount: number;
  totalCount: number;
}
export interface OrderWelcomeIssued {
  code: string;
  ownerEmail?: string;
  devices: { unitId: string; registerId: string; registerName?: string; label: string }[];
  /** Order units with no register carrying their deviceUnitId — they ship on the old flow. */
  unpaired?: string[];
  /** Pay screens whose till is not in this order — shipped Fresh but NOT locked (nothing could unlock them). */
  uncoupled?: string[];
}
export interface RegisterWelcomeView {
  welcome: RegisterWelcomeRow | null;
  unitId?: string;
  orderId?: string;
  lock?: OrderWelcomeLockView | null;
}

/**
 * One row of an agreement's audit trail (§7.3). Written by the service in the
 * same transaction as the action itself, so an agreement can never change
 * without a row saying who did it, when, how much and why.
 */
export interface AgreementAuditRow {
  unitId: string;
  action: 'waive' | 'settle' | 'rollover';
  actor?: string;
  actorEmail?: string;
  reason: string;
  /** Server-computed: principalCents - paidCents at the moment of the action. */
  amountCents: number;
  rolledOverCents?: number;
  targetUnitId?: string;
  skuId?: string;
  principalCents: number;
  paidCents: number;
  agreementStatusBefore: string;
  agreementStatusAfter: string;
  createdAt: number;
}

/** What a unit status change may carry. Anything else is refused with a 400. */
export interface UnitStatusPatch {
  state: UnitStatus;
  shipment?: { carrier: string; trackingNumber: string; trackingUrl?: string };
  holdReason?: string;
  notes?: string;
}

/** One entry in the POS APK registry published by the POS deploy. */
export type PublishedPosBuild = {
  id: string;
  displayName: string;
  version: string;
  apkUrl: string;
  hash: string;
  versionCode: number;
  builtAt: number;
};

export const hardwareApi = {
  /**
   * The published POS APK registry for THIS console's environment.
   *
   * No `env` argument on purpose: the service answers for its own stage, so a
   * prod console sees prod builds and a dev console sees dev builds, and no
   * caller can ask for the other one. Read server side because the CDN that
   * holds the registry sends no CORS headers — see the handler's docblock.
   */
  listPosBuilds: () =>
    req<{ env: 'dev' | 'prod'; targets: PublishedPosBuild[]; generatedAt: number | null }>('/pos-builds'),

  listModels: (q: { category?: string; status?: string } = {}) => {
    const s = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString();
    return req<{ items: DeviceModelRow[]; count: number; invalid: string[] }>(`/models${s ? `?${s}` : ''}`);
  },
  getModel: (modelId: string) => req<{ model: DeviceModelRow }>(`/models/${encodeURIComponent(modelId)}`),
  upsertModel: (model: any) => req<{ model: DeviceModelRow }>('/models', { method: 'POST', body: JSON.stringify(model) }),
  deleteModel: (modelId: string) => req<{ deleted: string }>(`/models/${encodeURIComponent(modelId)}`, { method: 'DELETE' }),
  getModelUsage: (modelId: string) => req<ModelUsage>(`/models/${encodeURIComponent(modelId)}/usage`),

  /**
   * Per-unit note and cost.
   *
   * A MINIMAL body on purpose. `upsertUnit` merges `{...existing, ...body}`, so
   * sending only what changed leaves every other field alone. Round-tripping a
   * whole unit object loaded a moment earlier is how a concurrent provisioning
   * tick or box-pack gets silently reverted — the service pins its worst cases,
   * but not sending stale values at all is the cheaper guarantee.
   */
  /**
   * Ask for an override code. Emails a six-digit code to the CALLER (proving
   * the person acting is really them) and an alert — with no code in it — to
   * the super admin, so an override is never requested unseen.
   */
  requestUnitOverride: (unitId: string) =>
    req<{ requested: true; expiresAt: number; sentTo: string }>(
      `/units/${encodeURIComponent(unitId)}/override/request`, { method: 'POST', body: '{}' },
    ),

  /** Spend the code. Opens a short window in which this ONE unit may change. */
  verifyUnitOverride: (unitId: string, code: string) =>
    req<{ unlocked: true; unlockedUntil: number }>(
      `/units/${encodeURIComponent(unitId)}/override/verify`,
      { method: 'POST', body: JSON.stringify({ code }) },
    ),

  /**
   * Put a device back on the shelf.
   *
   * The DEPTH is the server's decision, from the unit's current state — a plain
   * release for one merely reserved, a wipe for one already configured, a
   * refusal once it has shipped. The caller never asks for a tier, so a UI
   * cannot talk the server into a deeper or shallower action than the state
   * allows. `reason` is required for a wipe and ignored otherwise.
   */
  releaseUnit: (unitId: string, reason?: string) =>
    req<{
      unit: DeviceUnitRow;
      tier: 'release' | 'wipe';
      cleared: string[];
    }>(`/units/${encodeURIComponent(unitId)}/release`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),

  saveUnitNoteAndCost: (
    unitId: string,
    modelId: string,
    patch: {
      notes?: string;
      notesRich?: DeviceUnitRow['notesRich'];
      badges?: string[];
      costCents?: number;
      costBreakdown?: DeviceUnitRow['costBreakdown'];
      /* Typed off the device's sticker (or scanned into the field) while the
         unit is being received. Goes through the same `/units` upsert as the
         note: `candidate = {...existing, ...body}` passes it straight to the
         stored row, so no new endpoint is involved. */
      serialNumber?: string;
    },
  ) =>
    req<{ unit: DeviceUnitRow }>('/units', {
      method: 'POST',
      body: JSON.stringify({ unitId, modelId, ...patch }),
    }),

  /**
   * Device photos. These live on hardware-service, NOT uploads-service:
   * uploads-service is gated by the TENANT Cognito pool, while super-admin
   * authenticates against the platform ADMIN pool, so its presign returns 401
   * from this app.
   */
  /**
   * Presign an upload. Handles photos, video and documents — the service
   * derives the kind from `contentType` and applies that kind's size cap, so
   * the caller never names its own limit.
   */
  presignPhoto: (body: { modelId?: string; filename: string; contentType: string; bytes?: number }) =>
    req<{ uploadUrl: string; fileUrl: string; key: string; kind?: 'image' | 'video' | 'doc' }>('/photos/presign', {
      method: 'POST', body: JSON.stringify(body),
    }),
  deletePhoto: (fileUrl: string) =>
    req<{ deleted: string }>('/photos', { method: 'DELETE', body: JSON.stringify({ fileUrl }) }),

  listBundles: () => req<{ items: DeviceBundleRow[]; count: number }>('/bundles'),
  getBundle: (bundleId: string) => req<{ bundle: DeviceBundleRow }>(`/bundles/${encodeURIComponent(bundleId)}`),
  upsertBundle: (bundle: any) => req<{ bundle: DeviceBundleRow }>('/bundles', { method: 'POST', body: JSON.stringify(bundle) }),
  deleteBundle: (bundleId: string) => req<{ deleted: string }>(`/bundles/${encodeURIComponent(bundleId)}`, { method: 'DELETE' }),

  /**
   * ARCHIVE / RESTORE a bundle. `upsertBundle` merges into the stored row, so a
   * status-only body is enough — nothing else about the kit is touched, and the
   * bundle keeps existing so every deal already sold against it still resolves
   * its name, photo and parts list. `eol` is the archived state (there is no
   * separate flag): archived kits are filtered out of the pickers that start
   * NEW work — deal editor, financing wizard, commission targets.
   */
  setBundleStatus: (bundleId: string, status: 'draft' | 'active' | 'eol') =>
    req<{ bundle: DeviceBundleRow }>('/bundles', {
      method: 'POST',
      body: JSON.stringify({ bundleId, status }),
    }),

  // -- Fulfilment ----------------------------------------------------------

  listOrders: (q: { tenantId?: string; status?: string } = {}) => {
    const s = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString();
    return req<{ items: DeviceOrderRow[]; count: number; invalid: string[] }>(`/orders${s ? `?${s}` : ''}`);
  },

  /** One order, its live derived status, and every device allocated to it. */
  getOrder: (orderId: string) =>
    req<{ order: DeviceOrderRow; units: DeviceUnitRow[]; invalidUnits: string[] }>(
      `/orders/${encodeURIComponent(orderId)}`,
    ),

  /**
   * The ONLY order status a client may set. Everything else is derived from the
   * units. Refused with a 409 once any device has shipped — check
   * `cancelBlockReason` before offering the button.
   *
   * `viaCancellationId` is additive: pass it when this cancel is the FIRST of
   * the two calls that close a cancellation record (see `CancellationPanel`).
   * The server matches it against the cancellation currently holding this
   * order — an id that does not match is refused with a 409, which is what
   * stops a stale tab from cancelling out from under a settlement someone
   * else already withdrew. Every other caller — the legacy "just cancel it"
   * button included — keeps calling this with one argument, unchanged.
   */
  cancelOrder: (orderId: string, opts?: { viaCancellationId?: string }) =>
    req<{ order: DeviceOrderRow }>(`/orders/${encodeURIComponent(orderId)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status: 'cancelled', ...(opts?.viaCancellationId ? { viaCancellationId: opts.viaCancellationId } : {}) }),
    }),

  /**
   * FIFO-allocates `in_stock` units of the right model(s) to an `awaiting`
   * order. Never over-allocates and never invents stock — a model with
   * fewer in_stock units than needed reports the gap as `shortQty` per
   * allocation line rather than reaching into a different model. A 200 is
   * NOT proof the order is now fully stocked: check `fullyAllocated` (W5
   * review I13 — this type used to declare `{ order, units }`, which the
   * server has never returned; the real shape is `{ orderId, allocations,
   * fullyAllocated, order }`, with no `units` at all).
   */
  allocateOrder: (
    orderId: string,
    preferUnitIds?: string[],
    /** Narrow to one device action — see allocateOrder in hardware-service. */
    opts?: { onlyModelIds?: string[]; onlyPreferred?: boolean },
  ) =>
    req<{
      orderId: string;
      allocations: {
        modelId: string;
        neededQty: number;
        alreadyAllocated: number;
        allocatedUnitIds: string[];
        allocatedCount: number;
        shortQty: number;
        trackingMode?: 'quantity';
        reservedQty?: number;
      }[];
      fullyAllocated: boolean;
      order: DeviceOrderRow | null;
    }>(
      `/orders/${encodeURIComponent(orderId)}/allocate`,
      // `preferUnitIds` REORDERS the server's FIFO walk so the units an
      // operator picked are tried first. It is not a second allocation path:
      // a unit that is not in stock, or is of a model this order does not
      // need, is ignored rather than forced in — so picking can never
      // double-allocate a device or invent stock.
      { method: 'POST', body: JSON.stringify({ ...(preferUnitIds?.length ? { preferUnitIds } : {}), ...(opts || {}) }) },
    ),

  /**
   * Every serial-numbered device, filterable by tenant / order / state / model.
   * The tenant Finance tab reads it scoped to one tenant. `limit`/`nextToken`
   * (B1 pagination contract) — omit `limit` for the old byte-for-byte
   * unpaginated response; pass it to page (the Stock tab's per-model
   * expansion always passes `limit: 25` rather than fetching every unit of a
   * model up front).
   */
  listUnits: (q: {
    tenantId?: string; orderId?: string; state?: string; modelId?: string;
    /**
     * Exact serial OR tracking id, matched server-side. Exists so a SCAN is one
     * request: allocation used to download every in-stock unit and search the
     * array. Trimmed and case-insensitive, but never a prefix match — a
     * half-read scan must not resolve to the wrong physical device.
     */
    serial?: string;
    limit?: number; nextToken?: string;
  } = {}) => {
    const s = new URLSearchParams(
      Object.entries(q).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]),
    ).toString();
    return req<{
      items: DeviceUnitRow[];
      /** This page's size. */
      count: number;
      /**
       * Matches BEFORE pagination. Lets a caller ask "how many are in stock?"
       * with `limit: 1` instead of downloading them all. Optional because a
       * server deployed before this field simply omits it.
       */
      total?: number;
      invalid: string[];
      nextToken?: string;
    }>(`/units${s ? `?${s}` : ''}`);
  },

  /**
   * One device WITH its agreement audit trail — an audit row nobody can read is
   * not an audit trail. Rows come back newest first.
   */
  getUnit: (unitId: string) =>
    req<{ unit: DeviceUnitRow; audit: AgreementAuditRow[] }>(`/units?unitId=${encodeURIComponent(unitId)}`),

  /** Create or amend a unit — allocating it to an order, or correcting its serial. */
  upsertUnit: (unit: Partial<DeviceUnitRow> & { modelId?: string }) =>
    req<{ unit: DeviceUnitRow }>('/units', { method: 'POST', body: JSON.stringify(unit) }),

  /**
   * Waive, settle early, or roll an agreement over onto a replacement (§7.3).
   *
   * The service computes the amount itself from `principalCents - paidCents`,
   * requires a reason, writes an audit row in the same transaction, and refuses
   * a second action on an agreement that is already settled or waived. NONE of
   * these are reversible through the API — check `agreementActionBlock` before
   * offering the button.
   */
  agreementAction: (unitId: string, body: { action: 'waive' | 'settle' | 'rollover'; reason: string; targetUnitId?: string }) =>
    req<{ unit: DeviceUnitRow; audit: AgreementAuditRow }>(
      `/units/${encodeURIComponent(unitId)}/agreement/action`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Move one device along the pipeline. The service refuses `shipped`/
   * `delivered`/`deployed` without carrier + tracking, and `on_hold`/
   * `backordered` without a reason — mirror those with `saveBlockReason`
   * rather than letting an operator fill in a form the server will reject.
   */
  setUnitStatus: (unitId: string, patch: UnitStatusPatch) =>
    req<{ unit: DeviceUnitRow; order: { status: OrderStatus; partial: boolean } | null }>(
      `/units/${encodeURIComponent(unitId)}/status`,
      { method: 'POST', body: JSON.stringify(patch) },
    ),

  /**
   * GET /units/{unitId}/label (wave 4) — everything the Rollo 2"×0.51" label
   * needs. `trackingId`/`serial`/`brand` are `null`, never fabricated, for a
   * unit minted before that field existed.
   */
  getUnitLabel: (unitId: string) =>
    req<{ unitId: string; trackingId: string | null; serial: string | null; modelName: string; brand: string | null }>(
      `/units/${encodeURIComponent(unitId)}/label`,
    ),

  // -- Lifecycle (inventory-hub-unit-lifecycle wave, B1+B2) -----------------
  // Every route below is `/lifecycle/...`, routed through hardware-service's
  // single `ANY /lifecycle/{proxy+}` Lambda — see b1-lifecycle-report.md.

  /**
   * RECORD A REPLACEMENT AS STARTED — the first act of the Replace wizard's
   * "Undo it all". The service stores the object as given (plus `startedAt`
   * and `actorEmail`) under `order.pendingReplacements[unitId]`; sending it
   * again overwrites it.
   */
  recordPendingReplacement: (orderId: string, body: Omit<PendingReplacementRow, 'startedAt' | 'actorEmail'>) =>
    req<{ order: DeviceOrderRow }>(
      `/lifecycle/orders/${encodeURIComponent(orderId)}/replacements`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** The last act of the hand-over. Idempotent — completing twice is not an error. */
  completePendingReplacement: (orderId: string, unitId: string, replacementUnitId?: string) =>
    req<{ order: DeviceOrderRow }>(
      `/lifecycle/orders/${encodeURIComponent(orderId)}/replacements/${encodeURIComponent(unitId)}/complete`,
      { method: 'POST', body: JSON.stringify(replacementUnitId ? { replacementUnitId } : {}) },
    ),

  /**
   * FIELD REPLACEMENT of a device that has already SHIPPED. The outgoing unit
   * keeps its `orderId` and moves to the reason's state (returned / lost / rma
   * / retired); an optional in-stock replacement of the SAME model is
   * allocated to the order with a `replaces` link. Pairing, register, reader
   * pool and VAR sheet are NOT touched — the caller hands those over.
   *
   * Billing is never adjusted: `billingReviewRequired` is always true, and the
   * caller must say so rather than imply it was handled.
   */
  replaceShippedUnit: (unitId: string, body: { reason: ReplacementReason; note?: string; replacementUnitId?: string }) =>
    req<{
      unit: DeviceUnitRow;
      replacement: DeviceUnitRow | null;
      order: DeviceOrderRow | null;
      warrantyAtClaim: { inWarranty: boolean; warrantyUntil?: number; daysLeft?: number | null };
      billingReviewRequired: true;
      billingNote: string;
    }>(`/lifecycle/units/${encodeURIComponent(unitId)}/replace`, { method: 'POST', body: JSON.stringify(body) }),

  /** Never 404s — synthesizes an empty Configuring/Testing/Packing default when nothing was ever saved. */
  getProvisioningTemplate: (modelId: string) =>
    req<{ template: ProvisioningTemplate }>(`/lifecycle/templates/${encodeURIComponent(modelId)}`),
  /** Wholesale replace — the editor always sends the FULL categories+steps list. */
  putProvisioningTemplate: (modelId: string, body: { categories: ProvisioningCategory[]; steps: ProvisioningStepInput[] }) =>
    req<{ template: ProvisioningTemplate }>(`/lifecycle/templates/${encodeURIComponent(modelId)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  /** Snapshots the model's saved (or synthesized) template onto the unit and moves it to `provisioning`/`configured`. */
  startProvisioning: (unitId: string) =>
    req<{ unit: DeviceUnitRow }>(`/lifecycle/units/${encodeURIComponent(unitId)}/provisioning/start`, { method: 'POST' }),
  /** Tick/untick one step. Auto-advances provisioning→configured when every required step is done, and reverts back if one is unticked. */
  tickProvisioningStep: (unitId: string, stepId: string, done: boolean) =>
    req<{ unit: DeviceUnitRow }>(`/lifecycle/units/${encodeURIComponent(unitId)}/provisioning/tick`, {
      method: 'POST', body: JSON.stringify({ stepId, done }),
    }),

  /**
   * Stamp a VERIFIED OS install onto a unit — the only door into `osInstall`.
   *
   * Called from the install panel on `/pos-config` with what the installer
   * itself reported, and only on a run that exited 0. Never call it with a
   * version somebody typed: the whole value of this field is that it cannot
   * be one, and a till's provisioning checklist unlocks on it.
   */
  recordOsInstall: (unitId: string, body: {
    appBuild: string;
    source: 'local' | 'dev' | 'prod';
    target: string;
    versionCode?: number;
    hash?: string;
    serial?: string;
    reset?: boolean;
  }) =>
    req<{ unit: DeviceUnitRow }>(`/lifecycle/units/${encodeURIComponent(unitId)}/os-install`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  /**
   * Send a till back to be built again — `configured`/`packing`/`provisioning`
   * → `allocated`, which is what re-queues it in Pending Configuration.
   *
   * Refused for anything that has shipped, and the refusal is physical rather
   * than procedural: the OS install runs over local adb from the operator's own
   * machine, so a device in the field cannot be reinstalled by anybody. Surface
   * the server's message verbatim — it names what to do instead.
   */
  requestReinstall: (unitId: string, reason?: string) =>
    req<{ unit: DeviceUnitRow; order: DeviceOrderRow | null }>(
      `/lifecycle/units/${encodeURIComponent(unitId)}/reinstall`,
      { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
    ),

  /**
   * THE PAIR'S RECEIPTS — recorded on its POS unit. See `DeviceUnitSchema.receipts`.
   *
   * The photo is already in S3 by the time this is called: the browser signs a
   * PUT with the uploads service and uploads straight there, exactly as the
   * order screen's single-receipt upload has always done. This records the KEY.
   */
  addReceipt: (unitId: string, body: {
    kind: 'test' | 'refund' | 'label'; key: string; url?: string; name?: string; contentType?: string;
  }) =>
    req<{ unit: DeviceUnitRow; receipt: DeviceUnitReceipt }>(
      `/lifecycle/units/${encodeURIComponent(unitId)}/receipts`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * EXTERNAL BARCODES — bought labels that resolve to this unit.
   *
   * The unit's RTA `trackingId` is untouched by all three: these are aliases,
   * so a device can be scanned off a cheap adhesive label today and off a
   * durable printed one later, with both reaching the same record.
   *
   * `link` 409s when the code is already on ANOTHER device — one code points
   * at exactly one unit, enforced by the service, not by the form.
   */
  linkBarcode: (unitId: string, code: string) =>
    req<{ unit: DeviceUnitRow; code: string; alreadyLinked: boolean }>(
      `/lifecycle/units/${encodeURIComponent(unitId)}/barcodes`,
      { method: 'POST', body: JSON.stringify({ code }) },
    ),

  unlinkBarcode: (unitId: string, code: string) =>
    req<{ unit: DeviceUnitRow; code: string }>(
      `/lifecycle/units/${encodeURIComponent(unitId)}/barcodes/${encodeURIComponent(code)}`,
      { method: 'DELETE' },
    ),

  /** One box for every number on a machine — bought label, RTA id, or serial. */
  resolveScan: (code: string) =>
    req<{ unit: DeviceUnitRow; matchedOn: 'externalBarcode' | 'trackingId' | 'serialNumber'; code: string }>(
      `/lifecycle/scan/${encodeURIComponent(code)}`,
    ),

  removeReceipt: (unitId: string, receiptId: string) =>
    req<{ unit: DeviceUnitRow }>(
      `/lifecycle/units/${encodeURIComponent(unitId)}/receipts/${encodeURIComponent(receiptId)}`,
      { method: 'DELETE' },
    ),

  /**
   * Re-run the provisioning advance that only ever ran when a step was ticked.
   *
   * A card device whose checklist is finished is held at `provisioning` until a
   * TID is keyed to it. That gate is right, but nothing re-evaluated it once the
   * TID arrived — so a unit keyed AFTER its last tick stayed `provisioning` for
   * good, and the payment lane sat at 80% with every step in it done.
   *
   * Safe to call on every order load: it only moves units forward, only ones
   * whose required steps are all done, and it runs the same gates the tick does.
   */
  recheckProvisioning: (orderId: string) =>
    req<{ advanced: string[]; stillBlocked: { unitId: string; reason: string }[] }>(
      `/lifecycle/orders/${encodeURIComponent(orderId)}/recheck-provisioning`,
      { method: 'POST' },
    ),

  /** Create a new box (omit boxNo to auto-increment) or update an existing one's carrier/tracking in place. */
  upsertBox: (orderId: string, body: { boxNo?: number; carrier?: string; trackingNumber?: string; trackingUrl?: string; weightGrams?: number }) =>
    req<{ order: DeviceOrderRow; box: DeviceOrderBox }>(`/lifecycle/orders/${encodeURIComponent(orderId)}/boxes`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  /** Pack one configured unit into a box — refuses with the server's own gate message (provisioning incomplete / agreement unsigned) verbatim. */
  packUnitIntoBox: (orderId: string, boxNo: number, unitId: string) =>
    req<{ unit: DeviceUnitRow; order: DeviceOrderRow | null }>(
      `/lifecycle/orders/${encodeURIComponent(orderId)}/boxes/${boxNo}/pack`,
      { method: 'POST', body: JSON.stringify({ unitId }) },
    ),
  /**
   * Pack a QUANTITY of a quantity-mode model into a box (same endpoint,
   * `{ modelId, qty }` instead of `{ unitId }`). Refuses packing more than
   * this order+model's remaining reservation — the server's message is
   * surfaced verbatim, same as the unit path. Writes a `packed`+`released`
   * movement pair; unlike the unit path there is no unit-level provisioning
   * check (no unit exists), only the order-level gates (cancelled order,
   * unsigned agreement).
   */
  packBulkIntoBox: (orderId: string, boxNo: number, modelId: string, qty: number) =>
    req<{ order: DeviceOrderRow; box: DeviceOrderBox; movements: StockMovement[] }>(
      `/lifecycle/orders/${encodeURIComponent(orderId)}/boxes/${boxNo}/pack`,
      { method: 'POST', body: JSON.stringify({ modelId, qty }) },
    ),
  /** Every box on the order with its DERIVED contents (unitIds), plus the units still unassigned to any box. */
  getPacking: (orderId: string) =>
    req<{ order: DeviceOrderRow; boxes: (DeviceOrderBox & { unitIds: string[] })[]; unassignedUnitIds: string[] }>(
      `/lifecycle/orders/${encodeURIComponent(orderId)}/packing`,
    ),

  /** Newest-first, paginated (same limit/nextToken contract as `listUnits`). Always starts with a synthesized "received" entry. */
  getUnitTimeline: (unitId: string, q: { limit?: number; nextToken?: string } = {}) => {
    const s = new URLSearchParams(
      Object.entries(q).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]),
    ).toString();
    return req<{ items: UnitEvent[]; count: number; nextToken?: string }>(
      `/lifecycle/units/${encodeURIComponent(unitId)}/timeline${s ? `?${s}` : ''}`,
    );
  },
  /** Admin-authored note/event, appended onto the unit's timeline. */
  addUnitEvent: (unitId: string, body: { kind: string; label: string; source?: 'system' | 'admin' | 'device'; meta?: Record<string, any> }) =>
    req<{ event: UnitEvent }>(`/lifecycle/units/${encodeURIComponent(unitId)}/events`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  // -- "Hello from Ruby" welcome wizard: the order's unlock code + each register's welcome --
  /** The order's shipping lock and every device's activation. `lock` is null until a code was issued. */
  getOrderWelcome: (orderId: string) =>
    req<OrderWelcomeView>(`/lifecycle/orders/${encodeURIComponent(orderId)}/welcome`),
  /**
   * Issues the order's unlock code and sets every paired register Fresh (+ Locked
   * unless `shipUnlocked`). Idempotent: the SAME code comes back while one exists.
   * `unpaired` = order units with no register carrying their deviceUnitId.
   */
  issueOrderWelcome: (orderId: string, body: { shipUnlocked?: boolean } = {}) =>
    req<OrderWelcomeIssued>(`/lifecycle/orders/${encodeURIComponent(orderId)}/welcome/issue`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  /** A new code; the old one stops working. Resets wrong tries and the hold. */
  newOrderWelcomeCode: (orderId: string) =>
    req<{ code: string }>(`/lifecycle/orders/${encodeURIComponent(orderId)}/welcome/new-code`, { method: 'POST' }),
  /** Stamps when and to whom the code went (history row on each unit). */
  markOrderWelcomeEmailed: (orderId: string, to: string) =>
    req<void>(`/lifecycle/orders/${encodeURIComponent(orderId)}/welcome/emailed`, {
      method: 'POST', body: JSON.stringify({ to }),
    }),
  getRegisterWelcome: (registerId: string, tenantId: string) =>
    req<RegisterWelcomeView>(
      `/lifecycle/registers/${encodeURIComponent(registerId)}/welcome?tenantId=${encodeURIComponent(tenantId)}`,
    ),
  /** fresh/setup flip the mode; lock/unlock flip the lock and keep the mode. */
  setRegisterWelcome: (registerId: string, tenantId: string, action: 'fresh' | 'setup' | 'lock' | 'unlock') =>
    req<{ welcome: RegisterWelcomeRow }>(
      `/lifecycle/registers/${encodeURIComponent(registerId)}/welcome?tenantId=${encodeURIComponent(tenantId)}`,
      { method: 'POST', body: JSON.stringify({ action }) },
    ),

  /**
   * Resolves a scanned/typed barcode. Tries `trackingId`/`serialNumber`
   * first (returns `{ unit }`); when nothing matches, ALSO tries every
   * model's `sharedBarcode` (returns `{ model }` instead — check which key
   * came back). Throws (404) when neither matches.
   */
  lookupUnitByCode: (code: string) =>
    req<{ unit?: DeviceUnitRow; model?: DeviceModelRow }>(`/lifecycle/units/lookup?code=${encodeURIComponent(code)}`),
};

/** Cents -> display dollars. One helper so no component divides by 100 inline. */
export const money = (cents: number | undefined | null): string =>
  `$${(((cents ?? 0)) / 100).toFixed(2)}`;

export const CATEGORY_LABEL: Record<string, string> = {
  pos_terminal: 'POS device',
  payment_device: 'Payment device',
  peripheral: 'Supporting device',
  accessory: 'Wires & accessories',
  office_supply: 'Office supply',
  marketing_material: 'Marketing material',
  tools: 'Tools',
};

export const CATEGORY_CHIP: Record<string, string> = {
  pos_terminal: 'bg-blue-100 text-blue-700',
  payment_device: 'bg-rose-100 text-rose-700',
  peripheral: 'bg-orange-100 text-orange-700',
  accessory: 'bg-gray-200 text-gray-700',
  office_supply: 'bg-teal-100 text-teal-700',
  marketing_material: 'bg-fuchsia-100 text-fuchsia-700',
  tools: 'bg-amber-100 text-amber-700',
};

/**
 * TERMINAL-TYPE WORDING — the shared map, GUARDED.
 *
 * `formFactorLabel` is a RUNTIME export of `@platform/shared-types`, and a Vite
 * dep pre-bundle built before it existed hands back `undefined`. Calling that
 * threw `formFactorLabel is not a function` inside a `.map()` and took the
 * whole Terminals tab down with it — a white screen for a LABEL.
 *
 * So the shared map is preferred and a local copy is the fallback. A duplicated
 * label table is a small cost; a screen that cannot render because the wording
 * table is briefly missing is not. The console names the actual remedy.
 */
const FORM_FACTOR_LABEL_FALLBACK: Record<string, string> = {
  pos: 'POS Terminal',
  kiosk: 'Self-Service Kiosk',
  customer_display: 'Customer Display',
  android_dual_screen: 'Android Dual-Screen POS Terminal',
  // Was MISSING these two and `android_single_screen`, which is how a stale
  // pre-bundle put the raw `kiosk_single_screen` on a tile in front of an
  // operator. A fallback table that does not cover every value is not a
  // fallback — it just moves the failure somewhere quieter.
  android_single_screen: 'Android Single Screen POS Terminal',
  kiosk_single_screen: 'Self-Service Kiosk — Single Screen',
  kiosk_dual_screen: 'Self-Service Kiosk — Dual Screen',
  not_applicable: 'Not Applicable',
  online_sales: 'Online Sales',
};

export const FORM_FACTOR_LABEL: Record<string, string> =
  (FORM_FACTOR_LABEL_RAW && typeof FORM_FACTOR_LABEL_RAW === 'object')
    ? FORM_FACTOR_LABEL_RAW
    : FORM_FACTOR_LABEL_FALLBACK;

export function formFactorLabel(v?: string): string {
  if (typeof formFactorLabelRaw === 'function') {
    const viaShared = formFactorLabelRaw(v);
    // `formFactorLabelRaw` returns the RAW VALUE when its own map has no entry,
    // so "it is a function" is not proof the pre-bundle is current: a stale one
    // exports the old function over the old table and hands back
    // `kiosk_single_screen` for a value it has simply never heard of. Take the
    // local table's word whenever the shared one only echoed the input back.
    if (!v || viaShared !== v) return viaShared;
    if (FORM_FACTOR_LABEL_FALLBACK[v]) return FORM_FACTOR_LABEL_FALLBACK[v];
    return viaShared;
  }
  // eslint-disable-next-line no-console
  console.error(
    '@platform/shared-types exported no formFactorLabel — a stale Vite pre-bundle. '
    + 'RESTART the dev server: deleting node_modules/.vite under a running server does nothing.',
  );
  if (!v) return '—';
  return FORM_FACTOR_LABEL[v] ?? v;
}

export const STATUS_CHIP: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  draft: 'bg-gray-200 text-gray-600',
  pilot: 'bg-sky-100 text-sky-700',
  eol: 'bg-amber-100 text-amber-700',
  discontinued: 'bg-red-100 text-red-700',
};

// ---------------------------------------------------------------------------
// FULFILMENT GUARDS — a mirror of the service's own rules (Task B3), so the UI
// never offers an operator a control the server will refuse. The server stays
// the enforcement point; nothing here is the only guard.
// ---------------------------------------------------------------------------

/**
 * Where each status sits in a device's life, and what entering it costs.
 *
 * This is the ONLY place the vocabulary is classified, and it is keyed
 * `Record<UnitStatus, …>` — not `Partial` — on purpose: a status added to
 * `UNIT_STATUSES` is a COMPILE ERROR here, so it cannot slip into the system
 * and silently vanish from the status bar, the filter pills or the
 * pre-dispatch rule. Every partition below is then FILTERED OUT OF
 * `UNIT_STATUSES` itself, so both the membership and the ORDER come from
 * shared-types. There is no second copy of the list to drift out of step.
 */
interface StatusFacts {
  /** 'stock' = ours and unallocated · 'fulfilment' = the forward pipeline · 'exception' = a recorded problem. */
  kind: 'stock' | 'fulfilment' | 'exception';
  /** Entering it requires a reason, and the customer is shown that reason (§7.2a). */
  reasonRequired?: true;
}

const STATUS_FACTS: Record<UnitStatus, StatusFacts> = {
  in_stock: { kind: 'stock' },
  allocated: { kind: 'stock' },
  awaiting: { kind: 'fulfilment' },
  processing: { kind: 'fulfilment' },
  // inventory-hub-unit-lifecycle wave — the two new per-model provisioning
  // stops (B1). They sit in the forward pipeline between processing and
  // packing (same order as UNIT_STATUSES), so `kind: 'fulfilment'` is what
  // makes them show up in FULFILMENT_FLOW / the order-detail segment bar
  // instead of silently vanishing from every Set built off that list.
  provisioning: { kind: 'fulfilment' },
  configured: { kind: 'fulfilment' },
  packing: { kind: 'fulfilment' },
  shipped: { kind: 'fulfilment' },
  delivered: { kind: 'fulfilment' },
  deployed: { kind: 'fulfilment' },
  on_hold: { kind: 'exception', reasonRequired: true },
  backordered: { kind: 'exception', reasonRequired: true },
  returned: { kind: 'exception' },
  rma: { kind: 'exception' },
  retired: { kind: 'exception' },
  // A unit nobody can find. A reason is required and the customer is shown it —
  // "lost" without saying how is not an answer anyone can act on.
  lost: { kind: 'exception', reasonRequired: true },
};

const byKind = (kind: StatusFacts['kind']): UnitStatus[] =>
  UNIT_STATUSES.filter((s) => STATUS_FACTS[s].kind === kind);

/** The forward pipeline (§7.2a), in `UNIT_STATUSES` order. Rendered as the per-device status bar. */
export const FULFILMENT_FLOW: UnitStatus[] = byKind('fulfilment');

/** How a real problem gets recorded. Reachable from ANY state, by design. */
export const EXCEPTION_FLOW: UnitStatus[] = byKind('exception');

/** Ours, and not on an order. Shown as filter pills so no order is unreachable. */
export const STOCK_FLOW: UnitStatus[] = byKind('stock');

/**
 * The pipeline splits at 'shipped': everything from there on implies the device
 * physically left us, and everything before it (plus stock) is somewhere it can
 * never claim to be again once it has. Sliced rather than listed, so a
 * fulfilment state added on either side of that line lands on the right side of
 * the rule by construction.
 */
const SHIPPED_AT = FULFILMENT_FLOW.indexOf('shipped');
const DISPATCHED = new Set<UnitStatus>(FULFILMENT_FLOW.slice(SHIPPED_AT));
const PRE_DISPATCH = new Set<UnitStatus>([...STOCK_FLOW, ...FULFILMENT_FLOW.slice(0, SHIPPED_AT)]);

const EXCEPTIONS = new Set<UnitStatus>(EXCEPTION_FLOW);
const FULFILMENT = new Set<UnitStatus>(FULFILMENT_FLOW);

/**
 * A carrier and a tracking number are required to enter any of these. It is the
 * same set as DISPATCHED and that is not a coincidence: a device that has left
 * us is exactly a device there is something to track.
 */
export const SHIPMENT_REQUIRED = DISPATCHED;

/** A reason is required here — and the customer is shown it. */
export const REASON_REQUIRED = new Set<UnitStatus>(
  UNIT_STATUSES.filter((s) => STATUS_FACTS[s].reasonRequired),
);

export const UNIT_STATUS_LABEL: Record<UnitStatus, string> = {
  in_stock: 'In stock',
  allocated: 'Allocated',
  awaiting: 'Awaiting',
  processing: 'Processing',
  provisioning: 'Provisioning',
  configured: 'Configured',
  packing: 'Packing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  deployed: 'Deployed',
  on_hold: 'On hold',
  backordered: 'Backordered',
  returned: 'Returned',
  rma: 'RMA',
  retired: 'Retired',
  lost: 'Lost',
};

/** Whole class strings so Tailwind's scanner can see them — never interpolate these. */
export const UNIT_STATUS_CHIP: Record<UnitStatus, string> = {
  in_stock: 'bg-gray-200 text-gray-700',
  allocated: 'bg-gray-200 text-gray-700',
  awaiting: 'bg-gray-200 text-gray-700',
  processing: 'bg-amber-100 text-amber-700',
  provisioning: 'bg-rose-100 text-rose-700',
  configured: 'bg-rose-100 text-rose-700',
  packing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-rose-100 text-rose-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  deployed: 'bg-emerald-100 text-emerald-700',
  on_hold: 'bg-red-100 text-red-700',
  backordered: 'bg-red-100 text-red-700',
  returned: 'bg-orange-100 text-orange-700',
  rma: 'bg-orange-100 text-orange-700',
  retired: 'bg-gray-300 text-gray-700',
  lost: 'bg-red-100 text-red-700',
};

/** An order can also read 'cancelled', which no unit ever derives. */
export const ORDER_STATUS_LABEL: Record<string, string> = { ...UNIT_STATUS_LABEL, cancelled: 'Cancelled' };
export const ORDER_STATUS_CHIP: Record<string, string> = { ...UNIT_STATUS_CHIP, cancelled: 'bg-gray-800 text-gray-100' };

/**
 * Has this device ever physically left us?
 *
 * NOT a question about its current state — `returned`, `rma`, `on_hold` and
 * `backordered` all drop a unit out of the pipeline while the device is still
 * out there. `shipment.shippedAt` is the evidence and is never cleared once
 * stamped, so an incorrect one is permanent: the UI must never offer a control
 * implying a shipped device can go back.
 */
export const hasShipped = (unit: Pick<DeviceUnitRow, 'state' | 'shipment'> | undefined): boolean =>
  !!unit?.shipment?.shippedAt || DISPATCHED.has(unit?.state as UnitStatus);

/**
 * Is `candidate` less advanced than `than`?
 *
 * shared-types owns the ranking but keeps it module-private. `deriveOrderStatus`
 * returns the least-advanced of the states it is given, so asking it about a
 * pair reads the SAME ranking through its public door — no second rank table
 * to drift out of step with the server.
 */
export const isLessAdvanced = (candidate: UnitStatus, than: UnitStatus): boolean =>
  candidate !== than && deriveOrderStatus([candidate, than]).status === candidate;

/**
 * Why the service would refuse moving this device to `target`, or null when it
 * would accept it. Used to DISABLE segments, not just to complain afterwards.
 */
export function unitTransitionBlock(
  unit: Pick<DeviceUnitRow, 'state' | 'shipment'> | undefined,
  target: UnitStatus,
  orderStatus?: OrderStatus,
): string | null {
  if (orderStatus === 'cancelled' && FULFILMENT.has(target)) {
    return 'This order is cancelled, so a device on it cannot move through fulfilment. Wind it down instead — return it to stock, or record a problem.';
  }
  if (!unit) return null;
  // Exception states stay open from anywhere: they are how a real problem gets
  // recorded, and a rule that stopped an operator recording what happened would
  // be worse than the bug it prevents.
  if (EXCEPTIONS.has(target)) return null;

  if (hasShipped(unit) && PRE_DISPATCH.has(target)) {
    return `This device has already left us, so it cannot go back to "${UNIT_STATUS_LABEL[target]}". Record what actually happened instead: ${EXCEPTION_FLOW.map((s) => UNIT_STATUS_LABEL[s]).join(', ')}.`;
  }
  if (DISPATCHED.has(unit.state) && isLessAdvanced(target, unit.state)) {
    return `A device that is "${UNIT_STATUS_LABEL[unit.state]}" cannot be moved back to "${UNIT_STATUS_LABEL[target]}" — progress cannot be un-claimed.`;
  }
  return null;
}

/** One device's pending change, as the screen has it before Save. */
export interface UnitIntent {
  unit: DeviceUnitRow;
  state: UnitStatus;
  holdReason: string;
}

/** The dispatch details typed once for the order and applied to what ships. */
export interface ShipmentDraft {
  carrier: string;
  trackingNumber: string;
  trackingUrl?: string;
}

/**
 * Why Save must stay disabled, or null when the whole batch would be accepted.
 *
 * Save is disabled until a `shipped` status has BOTH a carrier and a tracking
 * number: a shipped status with nowhere to track tells a customer to expect
 * something and gives them no way to check. Hold and backorder are disabled
 * until a reason is typed, because that reason IS the message the customer gets.
 */
export function saveBlockReason(
  intents: UnitIntent[],
  shipment: ShipmentDraft,
  orderStatus?: OrderStatus,
): string | null {
  const carrier = shipment.carrier.trim();
  const tracking = shipment.trackingNumber.trim();

  for (const intent of intents) {
    const blocked = unitTransitionBlock(intent.unit, intent.state, orderStatus);
    if (blocked) return blocked;

    if (SHIPMENT_REQUIRED.has(intent.state)) {
      // The device's own shipment counts: you should not have to re-key a
      // carrier to move something already shipped along to delivered.
      const own = intent.unit.shipment;
      const haveCarrier = carrier || own?.carrier?.trim();
      const haveTracking = tracking || own?.trackingNumber?.trim();
      if (!haveCarrier || !haveTracking) {
        return `"${UNIT_STATUS_LABEL[intent.state]}" needs a carrier and a tracking number. A shipped status with nowhere to track is worse than none at all.`;
      }
    }

    if (REASON_REQUIRED.has(intent.state) && !intent.holdReason.trim()) {
      return `"${UNIT_STATUS_LABEL[intent.state]}" needs a reason — the customer is shown it, so a device may never be silently stuck.`;
    }
  }
  return null;
}

/**
 * Which shipment to send with a unit, so one device's tracking number can never
 * be overwritten by another's.
 *
 * The typed draft is applied when the device is entering a shipped state
 * WITHOUT tracking of its own. A device that already has complete tracking keeps
 * it — sending nothing leaves the stored shipment untouched — unless `governs`
 * says the draft describes this device too, which is only true when the fields
 * were prefilled from a shipment every shipped device shares AND the operator
 * has since edited them. Devices that shipped separately are never rewritten
 * from a form that describes one of them.
 */
export function shipmentForIntent(
  intent: UnitIntent,
  shipment: ShipmentDraft,
  governs: boolean,
): ShipmentDraft | undefined {
  if (!SHIPMENT_REQUIRED.has(intent.state)) return undefined;
  const own = intent.unit.shipment;
  const complete = !!(own?.carrier?.trim() && own?.trackingNumber?.trim());
  if (complete && !governs) return undefined;
  const carrier = shipment.carrier.trim() || own?.carrier || '';
  const trackingNumber = shipment.trackingNumber.trim() || own?.trackingNumber || '';
  const trackingUrl = shipment.trackingUrl?.trim() || own?.trackingUrl;
  return { carrier, trackingNumber, ...(trackingUrl ? { trackingUrl } : {}) };
}

/**
 * Why the order cannot be cancelled, or null when it can.
 *
 * Keyed on whether a device ever shipped, not on its current state — a unit
 * that shipped and was then returned has still left us, and the service refuses
 * the cancellation with a 409.
 */
export function cancelBlockReason(
  order: Pick<DeviceOrderRow, 'status'>,
  units: DeviceUnitRow[],
): string | null {
  if (order.status === 'cancelled') return 'This order is already cancelled.';
  const gone = units.filter(hasShipped);
  if (gone.length === 0) return null;
  return `${gone.length} device${gone.length === 1 ? ' has' : 's have'} already left us, so this order can no longer be cancelled. Record a return or an RMA on the device instead.`;
}

export interface DerivedStatusView {
  status: OrderStatus;
  partial: boolean;
  /** Just the status as a human reads it, e.g. `Backordered (partial)`. */
  label: string;
  headline: string;
  detail: string;
}

/**
 * The order's status derived from a set of device states, with the `partial`
 * flag spelled out in words — so nobody reads "shipped" and misses that a
 * device is still backordered.
 *
 * Callers must be explicit about WHICH states they pass. Handing it a set of
 * unsaved drafts produces a preview, and a preview labelled as fact is the
 * design's central failure — being told a kit is on its way while part of it is
 * not — merely relocated from the customer to the operator.
 */
export function derivedOrderStatusView(
  storedStatus: OrderStatus | undefined,
  states: UnitStatus[],
): DerivedStatusView {
  if (storedStatus === 'cancelled') {
    return {
      status: 'cancelled', partial: false, label: 'Cancelled',
      headline: 'Order status: Cancelled',
      detail: 'Cancellation is the one status set directly, and only before anything has shipped. No device on this order may move through fulfilment.',
    };
  }
  if (states.length === 0) {
    return {
      status: 'awaiting', partial: false, label: 'Awaiting',
      headline: 'Order status: Awaiting',
      detail: 'No devices have been allocated to this order yet, so there is nothing for the status to be derived from.',
    };
  }

  const { status, partial } = deriveOrderStatus(states);
  const name = UNIT_STATUS_LABEL[status];
  const label = `${name}${partial ? ' (partial)' : ''}`;
  const detail = partial
    ? `Derived from the LEAST-ADVANCED device — the others are further ahead, which is what "partial" means. The order reads "${name}" because at least one device is still there, so the customer is never told the kit is on its way while part of it is not.`
    : `Derived from the devices above, and every one of them is "${name}". Order status is never set directly.`;
  return { status, partial, label, headline: `Order status: ${label}`, detail };
}

/**
 * How the money on an order reads to a human. Mirrors the deal-card wording.
 *
 * Monthly money is grouped BY TERM, never summed across terms. Two devices on
 * different terms are two different commitments, and folding them into a single
 * "× N" states the wrong term for one of them — the shorter one would appear to
 * run months longer than the customer actually signed up for. An open-ended
 * rental has no term and gets no "× N" at all.
 */
export interface OrderMoney {
  /**
   * Every recurring line on the order, summed.
   *
   * NO TERM IS STATED, and that is the difference between this and
   * `orderBillingLabel`. Summing money across terms is correct — it is what
   * leaves the customer's account each month — while summing the TERMS is not,
   * which is why the label below groups by term and this does not attempt to
   * name one.
   */
  monthlyCents: number;
  /** Outright purchases and down payments — everything due once. */
  onceCents: number;
  /** True when nothing on the order is chargeable: every item ships included. */
  includedOnly: boolean;
}

/**
 * The order's money as NUMBERS, for callers that have to add it to something
 * else — the header's total subscription adds it to the software half.
 *
 * `orderBillingLabel` is the same walk rendered as words and stays the answer
 * wherever the hardware stands alone; this is the arithmetic underneath it, so
 * the two cannot disagree about what an order costs.
 */
export function orderMoney(items: DeviceOrderItemRow[]): OrderMoney {
  let monthlyCents = 0;
  let onceCents = 0;
  let includedOnly = true;

  for (const item of items || []) {
    const qty = item.quantity || 1;
    if (item.saleMode === 'included') continue;
    includedOnly = false;
    if (item.saleMode === 'outright') { onceCents += item.amountCents * qty; continue; }
    monthlyCents += item.amountCents * qty;
  }
  return { monthlyCents, onceCents, includedOnly: includedOnly && (items || []).length > 0 };
}

export function orderBillingLabel(items: DeviceOrderItemRow[]): string {
  if (!items || items.length === 0) return 'No items';

  const OPEN = 'open';
  const monthly = new Map<number | typeof OPEN, number>();
  let onceCents = 0;
  let includedOnly = true;

  for (const item of items) {
    const qty = item.quantity || 1;
    if (item.saleMode === 'included') continue;
    includedOnly = false;
    if (item.saleMode === 'outright') {
      onceCents += item.amountCents * qty;
      continue;
    }
    const term = item.termMonths ?? OPEN;
    monthly.set(term, (monthly.get(term) || 0) + item.amountCents * qty);
  }

  if (includedOnly) return 'Included free';
  const parts: string[] = [];
  monthly.forEach((cents, term) => {
    if (cents > 0) parts.push(`${money(cents)}/mo${term === OPEN ? '' : ` × ${term}`}`);
  });
  if (onceCents > 0) parts.push(`${money(onceCents)} one-time`);
  return parts.length ? parts.join(' + ') : money(0);
}

/** "22 Aug 2026" — the format the fulfilment mockup uses. */
export const shortDate = (ts: number | undefined): string =>
  ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
